import {composeCellKey} from '../provider/cell-key'
import type {CellAddress, ItemId} from '../types'
import type {BoardSelectedCard, BoardSelectionIntent} from './types'

/**
 * One selected card as a single string, so "is this card selected" is answered in
 * constant time by every card on the board rather than by scanning the caller's list.
 *
 * Built on {@link composeCellKey} and length-prefixed the same way, because item, column
 * and row keys are all opaque and may hold any character, including the separator. A
 * collision here would be two different cards sharing one answer.
 *
 * Never parsed back: everything that needs the parts already holds the card.
 */
export function selectionKey({columnKey, rowKey, itemId}: BoardSelectedCard): string {
  return `${composeCellKey({columnKey, rowKey})}:${itemId.length}:${itemId}`
}

/** Whether two selections name the same cards. A selection is a set, so order is not part of it. */
export function isSameSelection(one: readonly BoardSelectedCard[], other: readonly BoardSelectedCard[]): boolean {
  if (one.length !== other.length) return false

  const keys = new Set(one.map(selectionKey))

  return other.every(card => keys.has(selectionKey(card)))
}

export type NextSelection = {
  selectedCards: readonly BoardSelectedCard[]
  /** The card a later `range` runs from, or `null` when there is nowhere to run one from. */
  rangeStart: BoardSelectedCard | null
}

export type NextSelectionInput = {
  /** What the caller states is selected right now. */
  selectedCards: readonly BoardSelectedCard[]
  /** The card the gesture was made on. */
  card: BoardSelectedCard
  intent: BoardSelectionIntent
  rangeStart: BoardSelectedCard | null
  /**
   * The cards one cell holds, in the order the caller stated them.
   *
   * The caller's data rather than the page, which is what lets a range cover cards a
   * windowed cell never drew.
   */
  itemIdsAt: (address: CellAddress) => readonly ItemId[]
}

/**
 * What one gesture makes of a selection, as a plain function of what it was given.
 *
 * Nothing here reads live state, so a whole gesture sequence can be reasoned about, and
 * tested, without a board being rendered at all. It returns the next selection rather
 * than applying one, because the board holds no selection: the caller states what is
 * selected and the caller is handed what a gesture would make of it.
 */
export function nextSelection({selectedCards, card, intent, rangeStart, itemIdsAt}: NextSelectionInput): NextSelection {
  if (intent === 'range') {
    const range = cardsBetween(rangeStart, card, itemIdsAt)
    // A range that has nowhere to run from falls through and starts one here. That is
    // what a shift-click in a different cell does, and it is the whole of the rule: the
    // gesture is answered inside one cell or not at all, never across two.
    if (range !== null) return {selectedCards: range, rangeStart}
  }

  // A toggle reaching into a different cell is the same boundary as a range crossing one,
  // and it is answered the same way: a selection is a set of cards in one cell, so
  // starting one somewhere else is starting a new selection rather than widening this one.
  if (intent === 'toggle' && isInSameCell(selectedCards, card)) {
    const key = selectionKey(card)
    const without = selectedCards.filter(selected => selectionKey(selected) !== key)
    const wasSelected = without.length !== selectedCards.length
    const next = wasSelected ? without : [...selectedCards, card]

    // A toggle that emptied the selection leaves nowhere to run a range from. The card it
    // took out is the one the reader has just said they did not want, and a selection
    // holding nothing is the same selection however it came to be empty, so an anchor
    // surviving one would also survive a caller clearing the selection itself.
    return {selectedCards: next, rangeStart: next.length === 0 ? null : card}
  }

  return {selectedCards: [card], rangeStart: card}
}

/**
 * Whether this card belongs to the same cell as what is already selected.
 *
 * True when nothing is selected, because there is no other cell to disagree with. Only
 * the first selected card is read: every gesture keeps a selection inside one cell, so
 * they are all in the same one.
 */
function isInSameCell(selectedCards: readonly BoardSelectedCard[], card: BoardSelectedCard): boolean {
  const first = selectedCards[0]
  if (first === undefined) return true

  return first.columnKey === card.columnKey && first.rowKey === card.rowKey
}

/**
 * Every card from one to the other, or `null` when this cell cannot answer the range.
 *
 * Three ways it cannot, and all three mean the same thing to the caller: there is no
 * range here, so the gesture starts one instead. Nothing to run from, a card to run from
 * that is in another cell, and a card the cell no longer holds because the caller's data
 * moved underneath the gesture.
 */
function cardsBetween(
  rangeStart: BoardSelectedCard | null,
  card: BoardSelectedCard,
  itemIdsAt: (address: CellAddress) => readonly ItemId[],
): readonly BoardSelectedCard[] | null {
  if (rangeStart === null) return null
  if (rangeStart.columnKey !== card.columnKey || rangeStart.rowKey !== card.rowKey) return null

  const itemIds = itemIdsAt(card)
  const from = itemIds.indexOf(rangeStart.itemId)
  const to = itemIds.indexOf(card.itemId)
  if (from === -1 || to === -1) return null

  return itemIds
    .slice(Math.min(from, to), Math.max(from, to) + 1)
    .map(itemId => ({columnKey: card.columnKey, rowKey: card.rowKey, itemId}))
}

/**
 * One selected card as a string, for a caller building its own lookup over a selection.
 *
 * The same function {@link selectionKey} is, exported under a name that says which
 * package it belongs to. A caller that holds a selection and needs to answer "is this one
 * in it" more than a handful of times wants a `Set` of these rather than a scan, and
 * building the key by hand is how two cards end up sharing one: the keys are opaque and
 * may hold any character, including the separator.
 *
 * Most callers want `useBoardSelection().isSelected` instead, which does exactly this.
 */
export const boardSelectionKey = selectionKey
